import { useState, useRef } from "react";
import axios from "axios";
import { useAdmin, resolveImgUrl } from "./AdminContext";
import { Plus, Pencil, Trash2, Calendar, Clock, MapPin, Globe, ToggleLeft, QrCode, ExternalLink, Copy, Check, Settings2, Eye, EyeOff, Asterisk, GripVertical, X, ImagePlus, Loader2, ChevronUp, ChevronDown, Mail, Home, List } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LANDING_FIELDS = [
  { key: "name", label: "Full Name", visible: true, required: true, type: "text" },
  { key: "email", label: "Email", visible: true, required: true, type: "email" },
  { key: "phone", label: "Phone", visible: true, required: false, type: "phone" },
  { key: "address", label: "Address", visible: true, required: false, type: "text" },
  { key: "customization_text", label: "Engraving Text", visible: true, required: false, type: "text" },
  { key: "customer_notes", label: "Special Notes", visible: true, required: false, type: "textarea" },
];

export function AdminEvents() {
  const { events, setEditingEvent, setEventForm, setEventDialogOpen, handleDeleteEvent, toggleEventActive, products, categories, fetchEvents } = useAdmin();

  const [landingDialogOpen, setLandingDialogOpen] = useState(false);
  const [landingEvent, setLandingEvent] = useState(null);
  const [landingForm, setLandingForm] = useState({});
  const [generatingQr, setGeneratingQr] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const bgFileRef = useRef(null);

  const openLandingConfig = (event) => {
    setLandingEvent(event);
    setLandingForm({
      landing_enabled: event.landing_enabled || false,
      landing_product_id: event.landing_product_id || "",
      landing_engraving_id: event.landing_engraving_id || "",
      landing_description: event.landing_description || "",
      landing_payment_mode: event.landing_payment_mode || "none",
      landing_customization_mode: event.landing_customization_mode || "info_only",
      landing_price: event.landing_price ?? "",
      landing_fields: event.landing_fields?.length ? event.landing_fields : DEFAULT_LANDING_FIELDS.map(f => ({ ...f })),
      landing_background_url: event.landing_background_url || "",
      vcard_qr_enabled: event.vcard_qr_enabled || false,
      email_notifications_enabled: event.email_notifications_enabled !== false,
      show_home_button: event.show_home_button !== false,
      hide_homepage_link: event.hide_homepage_link || false,
    });
    setLandingDialogOpen(true);
  };

  const saveLandingConfig = async () => {
    if (!landingEvent) return;
    setSaving(true);
    try {
      await axios.put(`${API}/events/${landingEvent.id}`, landingForm);
      fetchEvents();
      setLandingDialogOpen(false);
    } catch (e) {  } finally { setSaving(false); }
  };

  const generateQr = async (eventId) => {
    setGeneratingQr(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/generate-qr`, { frontend_url: window.location.origin });
      fetchEvents();
      if (landingEvent) setLandingEvent(prev => ({ ...prev, landing_qr_base64: res.data.qr_base64, landing_qr_url: res.data.url }));
    } catch (e) {  } finally { setGeneratingQr(false); }
  };

  const copyLandingUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const updateField = (idx, key, value) => {
    setLandingForm(f => {
      const fields = [...f.landing_fields];
      fields[idx] = { ...fields[idx], [key]: value };
      return { ...f, landing_fields: fields };
    });
  };

  const removeField = (idx) => {
    setLandingForm(f => ({ ...f, landing_fields: f.landing_fields.filter((_, i) => i !== idx) }));
  };

  const addCustomField = () => {
    const nextNum = (landingForm.landing_fields?.filter(f => f.key.startsWith("custom_")).length || 0) + 1;
    setLandingForm(f => ({
      ...f,
      landing_fields: [...(f.landing_fields || []), { key: `custom_${Date.now()}`, label: `Custom Field ${nextNum}`, visible: true, required: false, type: "text", custom: true }],
    }));
  };

  const moveField = (idx, direction) => {
    setLandingForm(f => {
      const fields = [...f.landing_fields];
      const target = idx + direction;
      if (target < 0 || target >= fields.length) return f;
      [fields[idx], fields[target]] = [fields[target], fields[idx]];
      return { ...f, landing_fields: fields };
    });
  };

  const updateDropdownOptions = (idx, options) => {
    updateField(idx, "options", options);
  };

  const addDropdownOption = (idx) => {
    const field = landingForm.landing_fields[idx];
    const opts = [...(field.options || []), ""];
    updateDropdownOptions(idx, opts);
  };

  const removeDropdownOption = (fieldIdx, optIdx) => {
    const field = landingForm.landing_fields[fieldIdx];
    const opts = (field.options || []).filter((_, i) => i !== optIdx);
    updateDropdownOptions(fieldIdx, opts);
  };

  const editDropdownOption = (fieldIdx, optIdx, value) => {
    const field = landingForm.landing_fields[fieldIdx];
    const opts = [...(field.options || [])];
    opts[optIdx] = value;
    updateDropdownOptions(fieldIdx, opts);
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !landingEvent) return;
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/events/${landingEvent.id}/upload-background`, fd);
      setLandingForm(f => ({ ...f, landing_background_url: res.data.landing_background_url }));
      setLandingEvent(prev => ({ ...prev, landing_background_url: res.data.landing_background_url }));
    } catch (err) {  } finally {
      setUploadingBg(false);
      if (bgFileRef.current) bgFileRef.current.value = "";
    }
  };

  const removeBgImage = () => {
    setLandingForm(f => ({ ...f, landing_background_url: "" }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase" data-testid="events-title">Events</h2>
          <p className="text-xs text-zinc-500 mt-1">Manage events and configure landing pages with QR codes.</p>
        </div>
        <button onClick={() => { setEditingEvent(null); setEventForm({ name: "", event_date: "", location: "", link: "" }); setEventDialogOpen(true); }} className="brutal-btn flex items-center gap-2 text-sm" data-testid="add-event-btn"><Plus size={16} strokeWidth={2.5} /> Add Event</button>
      </div>
      {events.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center" data-testid="no-events">
          <Calendar size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2} /><h3 className="font-heading font-bold text-sm uppercase mb-2">No Events Yet</h3>
          <p className="text-xs text-zinc-500">Add events where customers can pick up their orders.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="events-list">
          {events.map(event => (
            <div key={event.id} className="brutal-card overflow-hidden" data-testid={`event-${event.id}`}>
              <div className="p-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h3 className="font-heading font-bold text-sm uppercase">{event.name}</h3>
                    <span className={`text-[9px] font-heading font-bold uppercase px-2 py-0.5 border border-black ${event.active ? "bg-[#00E5FF]" : "bg-zinc-200 text-zinc-500"}`}>{event.active ? "Active" : "Inactive"}</span>
                    {event.landing_enabled && <span className="text-[9px] font-heading font-bold uppercase px-2 py-0.5 border border-[#FF3366] bg-[#FF3366] text-white">Landing Page</span>}
                  </div>
                  {event.event_date && <p className="text-xs text-zinc-500"><Clock size={10} className="inline mr-1" />{event.event_date}</p>}
                  {event.location && <p className="text-xs text-zinc-500"><MapPin size={10} className="inline mr-1" />{event.location}</p>}
                  {event.link && <p className="text-xs text-zinc-400 truncate"><Globe size={10} className="inline mr-1" /><a href={event.link} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#00E5FF]">{event.link}</a></p>}
                </div>
                {/* QR Code Preview */}
                {event.landing_qr_base64 && event.landing_enabled && (
                  <div className="flex-shrink-0 text-center">
                    <img src={`data:image/png;base64,${event.landing_qr_base64}`} alt="Event QR" className="w-20 h-20 border-2 border-black" />
                    <button onClick={() => copyLandingUrl(event.landing_qr_url)} className="text-[9px] text-[#FF3366] font-heading font-bold mt-1 flex items-center gap-1 hover:underline">
                      {copiedUrl ? <Check size={10} /> : <Copy size={10} />} {copiedUrl ? "Copied!" : "Copy URL"}
                    </button>
                  </div>
                )}
              </div>
              <div className="border-t-2 border-black px-5 py-3 bg-zinc-50 flex gap-2 flex-wrap">
                <button onClick={() => openLandingConfig(event)} className="brutal-btn text-xs px-3 py-2 flex items-center gap-1 bg-[#FF3366] text-white" data-testid={`config-landing-${event.id}`}><Settings2 size={12} strokeWidth={2.5} /> Landing Page</button>
                {event.landing_enabled && (
                  <a href={`/event/${event.id}`} target="_blank" rel="noopener noreferrer" className="brutal-btn brutal-btn-secondary text-xs px-3 py-2 flex items-center gap-1 no-underline" data-testid={`preview-landing-${event.id}`}><ExternalLink size={12} strokeWidth={2.5} /> Preview</a>
                )}
                <button onClick={() => toggleEventActive(event)} className={`brutal-btn text-xs px-3 py-2 ${event.active ? "brutal-btn-outline" : "brutal-btn-secondary"}`} data-testid={`toggle-event-${event.id}`}><ToggleLeft size={12} className="inline mr-1" strokeWidth={2.5} /> {event.active ? "Deactivate" : "Activate"}</button>
                <button onClick={() => { setEditingEvent(event); setEventForm({ name: event.name, event_date: event.event_date || "", location: event.location || "", link: event.link || "" }); setEventDialogOpen(true); }} className="brutal-btn brutal-btn-secondary text-xs px-3 py-2" data-testid={`edit-event-${event.id}`}><Pencil size={12} strokeWidth={2.5} /></button>
                <button onClick={() => handleDeleteEvent(event.id)} className="brutal-btn brutal-btn-outline text-xs px-3 py-2 border-red-500 text-red-500" data-testid={`delete-event-${event.id}`}><Trash2 size={12} strokeWidth={2.5} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Landing Page Config Dialog */}
      <Dialog open={landingDialogOpen} onOpenChange={setLandingDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="landing-config-dialog">
          <DialogHeader><DialogTitle className="font-heading font-bold text-lg uppercase">Landing Page — {landingEvent?.name}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Enable Toggle */}
            <div className="flex items-center gap-3 border-2 border-black p-4 bg-zinc-50">
              <Switch checked={landingForm.landing_enabled} onCheckedChange={v => setLandingForm(f => ({ ...f, landing_enabled: v }))} data-testid="landing-enabled-toggle" />
              <Label className="font-heading font-bold text-sm uppercase">{landingForm.landing_enabled ? "Landing Page Enabled" : "Landing Page Disabled"}</Label>
            </div>

            {/* Background Image */}
            <div className="border-2 border-black p-4 bg-zinc-50">
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-3">Background Image</label>
              {landingForm.landing_background_url ? (
                <div className="relative mb-3">
                  <img
                    src={resolveImgUrl(landingForm.landing_background_url)}
                    alt="Background preview"
                    className="w-full h-32 object-cover border-2 border-black"
                    data-testid="bg-image-preview"
                  />
                  <button
                    onClick={removeBgImage}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-red-500 text-white border-2 border-black hover:bg-red-600 transition-colors"
                    title="Remove background"
                    data-testid="remove-bg-btn"
                  >
                    <X size={14} strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <div className="w-full h-24 border-2 border-dashed border-zinc-300 flex items-center justify-center mb-3 text-zinc-300">
                  <ImagePlus size={28} />
                </div>
              )}
              <input type="file" ref={bgFileRef} accept="image/*" className="hidden" onChange={handleBgUpload} data-testid="bg-upload-input" />
              <button
                onClick={() => bgFileRef.current?.click()}
                disabled={uploadingBg}
                className="brutal-btn brutal-btn-outline text-xs px-4 py-2 w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="upload-bg-btn"
              >
                {uploadingBg ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : <><ImagePlus size={14} strokeWidth={2.5} /> {landingForm.landing_background_url ? "Replace Image" : "Upload Background"}</>}
              </button>
              <p className="text-[10px] text-zinc-400 mt-1">Recommended: wide landscape image (1920x800+). Applied behind the event page.</p>
            </div>

            {/* Product Selection */}
            <div>
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Product (everyone gets this) *</label>
              <select className="brutal-input w-full text-sm" value={landingForm.landing_product_id} onChange={e => setLandingForm(f => ({ ...f, landing_product_id: e.target.value }))} data-testid="landing-product-select">
                <option value="">Select a product...</option>
                {products.filter(p => p.active).map(p => (
                  <option key={p.id} value={p.id}>{p.name} — ${p.price?.toFixed(2)}</option>
                ))}
              </select>
            </div>

            {/* Engraving Selection */}
            <div>
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Engraving Style</label>
              <select className="brutal-input w-full text-sm" value={landingForm.landing_engraving_id} onChange={e => setLandingForm(f => ({ ...f, landing_engraving_id: e.target.value }))} data-testid="landing-engraving-select">
                <option value="">None / Not applicable</option>
                {categories.filter(c => c.active).map(c => (
                  <option key={c.id} value={c.id}>{c.name} — ${c.price?.toFixed(2)}</option>
                ))}
              </select>
            </div>

            {/* Price Override */}
            <div>
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Price Override</label>
              <input type="number" step="0.01" min="0" className="brutal-input w-full text-sm" placeholder="Leave blank for product + engraving price" value={landingForm.landing_price} onChange={e => setLandingForm(f => ({ ...f, landing_price: e.target.value }))} data-testid="landing-price-input" />
              <p className="text-[10px] text-zinc-400 mt-1">Set to 0 for free. Leave blank to auto-calculate.</p>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Payment Mode</label>
              <div className="flex gap-2">
                <button onClick={() => setLandingForm(f => ({ ...f, landing_payment_mode: "none" }))} className={`flex-1 text-xs px-3 py-3 border-2 border-black font-heading font-bold uppercase transition-all ${landingForm.landing_payment_mode === "none" ? "bg-green-500 text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`} data-testid="payment-mode-none">In-Person<br /><span className="text-[10px] font-normal normal-case">No online payment</span></button>
                <button onClick={() => setLandingForm(f => ({ ...f, landing_payment_mode: "stripe" }))} className={`flex-1 text-xs px-3 py-3 border-2 border-black font-heading font-bold uppercase transition-all ${landingForm.landing_payment_mode === "stripe" ? "bg-[#FF3366] text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`} data-testid="payment-mode-stripe">Stripe<br /><span className="text-[10px] font-normal normal-case">Online checkout</span></button>
              </div>
            </div>

            {/* vCard QR Toggle */}
            <div className="flex items-center gap-3 border-2 border-black p-4 bg-zinc-50">
              <Switch checked={landingForm.vcard_qr_enabled} onCheckedChange={v => setLandingForm(f => ({ ...f, vcard_qr_enabled: v }))} data-testid="vcard-qr-toggle" />
              <div>
                <Label className="font-heading font-bold text-sm uppercase">{landingForm.vcard_qr_enabled ? "vCard QR Enabled" : "vCard QR Disabled"}</Label>
                <p className="text-[10px] text-zinc-400 mt-0.5">Generate a QR code from customer info (name, phone, email, website) for each order.</p>
              </div>
            </div>

            {/* Email Notifications Toggle */}
            <div className="flex items-center gap-3 border-2 border-black p-4 bg-zinc-50">
              <Switch checked={landingForm.email_notifications_enabled} onCheckedChange={v => setLandingForm(f => ({ ...f, email_notifications_enabled: v }))} data-testid="email-notifications-toggle" />
              <div>
                <Label className="font-heading font-bold text-sm uppercase flex items-center gap-2">
                  {landingForm.email_notifications_enabled ? <><Mail size={14} /> Email Notifications On</> : <><Mail size={14} className="opacity-40" /> Email Notifications Off</>}
                </Label>
                <p className="text-[10px] text-zinc-400 mt-0.5">Send order confirmation emails for this event.</p>
              </div>
            </div>

            {/* Show Home Button Toggle */}
            <div className="flex items-center gap-3 border-2 border-black p-4 bg-zinc-50">
              <Switch checked={landingForm.show_home_button} onCheckedChange={v => setLandingForm(f => ({ ...f, show_home_button: v }))} data-testid="show-home-button-toggle" />
              <div>
                <Label className="font-heading font-bold text-sm uppercase flex items-center gap-2">
                  <Home size={14} /> {landingForm.show_home_button ? "Home Button Visible" : "Home Button Hidden"}
                </Label>
                <p className="text-[10px] text-zinc-400 mt-0.5">Show "Visit Main Site" button on order success screen.</p>
              </div>
            </div>

            {/* Hide Homepage Link Toggle */}
            <div className="flex items-center gap-3 border-2 border-black p-4 bg-zinc-50">
              <Switch checked={landingForm.hide_homepage_link} onCheckedChange={v => setLandingForm(f => ({ ...f, hide_homepage_link: v }))} data-testid="hide-homepage-link-toggle" />
              <div>
                <Label className="font-heading font-bold text-sm uppercase flex items-center gap-2">
                  <Globe size={14} /> {landingForm.hide_homepage_link ? "Homepage Link Hidden" : "Homepage Link Visible"}
                </Label>
                <p className="text-[10px] text-zinc-400 mt-0.5">Hide the "Order Now" button for this event on the main homepage.</p>
              </div>
            </div>

            {/* Form Fields Configuration */}
            <div className="border-2 border-black p-4 bg-zinc-50">
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-3">Form Fields</label>
              <p className="text-[10px] text-zinc-400 mb-3">Toggle visibility, mark required, edit labels, and reorder fields.</p>
              <div className="space-y-2">
                {(landingForm.landing_fields || []).map((field, idx) => (
                  <div key={field.key} className={`border-2 transition-all ${field.visible ? "border-black bg-white" : "border-zinc-200 bg-zinc-100 opacity-60"}`} data-testid={`field-config-${field.key}`}>
                    {/* Top row: reorder + toggles + label + type + remove */}
                    <div className="flex items-center gap-1.5 p-2.5">
                      {/* Reorder arrows */}
                      <div className="flex flex-col flex-shrink-0">
                        <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="w-5 h-4 flex items-center justify-center text-zinc-400 hover:text-black disabled:opacity-20 transition-colors" title="Move up" data-testid={`field-up-${field.key}`}>
                          <ChevronUp size={12} strokeWidth={3} />
                        </button>
                        <button onClick={() => moveField(idx, 1)} disabled={idx === (landingForm.landing_fields || []).length - 1} className="w-5 h-4 flex items-center justify-center text-zinc-400 hover:text-black disabled:opacity-20 transition-colors" title="Move down" data-testid={`field-down-${field.key}`}>
                          <ChevronDown size={12} strokeWidth={3} />
                        </button>
                      </div>
                      <button onClick={() => updateField(idx, "visible", !field.visible)} className={`flex-shrink-0 w-7 h-7 flex items-center justify-center border-2 border-black transition-all ${field.visible ? "bg-green-500 text-white" : "bg-zinc-200 text-zinc-400"}`} title={field.visible ? "Visible" : "Hidden"} data-testid={`field-visible-${field.key}`}>
                        {field.visible ? <Eye size={13} strokeWidth={2.5} /> : <EyeOff size={13} strokeWidth={2.5} />}
                      </button>
                      {field.type !== "heading" && (
                        <button onClick={() => updateField(idx, "required", !field.required)} disabled={!field.visible} className={`flex-shrink-0 w-7 h-7 flex items-center justify-center border-2 border-black transition-all disabled:opacity-30 ${field.required && field.visible ? "bg-[#FF3366] text-white" : "bg-white text-zinc-300"}`} title={field.required ? "Required" : "Optional"} data-testid={`field-required-${field.key}`}>
                          <Asterisk size={13} strokeWidth={2.5} />
                        </button>
                      )}
                      {/* Label input based on type */}
                      {field.type !== "heading" && field.type !== "boolean" && field.type !== "dropdown" && (
                        <input className="brutal-input flex-1 text-xs py-1.5 px-2" value={field.label} onChange={e => updateField(idx, "label", e.target.value)} placeholder="Field label" data-testid={`field-label-${field.key}`} />
                      )}
                      {field.type === "heading" && (
                        <input className="brutal-input flex-1 text-sm font-black uppercase py-1.5 px-2 text-[#FF3366] tracking-wider" value={field.label} onChange={e => updateField(idx, "label", e.target.value)} placeholder="Type your heading text..." data-testid={`field-label-${field.key}`} />
                      )}
                      {field.type === "boolean" && (
                        <span className="flex-1 text-[10px] uppercase font-bold text-zinc-400 tracking-wider">Yes / No Question</span>
                      )}
                      {field.type === "dropdown" && (
                        <span className="flex-1 text-[10px] uppercase font-bold text-zinc-400 tracking-wider flex items-center gap-1"><List size={11} /> Dropdown Menu</span>
                      )}
                      {field.custom && (
                        <select className="brutal-input text-[10px] py-1.5 px-1 w-24" value={field.type || "text"} onChange={e => updateField(idx, "type", e.target.value)} data-testid={`field-type-${field.key}`}>
                          <option value="text">Text</option>
                          <option value="email">Email</option>
                          <option value="textarea">Long Text</option>
                          <option value="phone">Phone</option>
                          <option value="heading">Heading</option>
                          <option value="boolean">Yes / No</option>
                          <option value="font">Font Pick</option>
                          <option value="dropdown">Dropdown</option>
                        </select>
                      )}
                      {field.custom && (
                        <button onClick={() => removeField(idx)} className="flex-shrink-0 w-7 h-7 flex items-center justify-center border-2 border-red-400 text-red-500 hover:bg-red-50 transition-all" title="Remove field" data-testid={`field-remove-${field.key}`}>
                          <X size={13} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                    {/* Boolean: question input */}
                    {field.type === "boolean" && (
                      <div className="px-2.5 pb-2.5 pt-0">
                        <input className="brutal-input w-full text-xs py-2 px-2" value={field.label} onChange={e => updateField(idx, "label", e.target.value)} placeholder="What are they answering Yes or No to? e.g. 'Want gift wrapping?'" data-testid={`field-label-${field.key}`} />
                      </div>
                    )}
                    {/* Dropdown: heading + editable options */}
                    {field.type === "dropdown" && (
                      <div className="px-2.5 pb-2.5 pt-0 space-y-2">
                        <input className="brutal-input w-full text-xs py-2 px-2 font-bold" value={field.label} onChange={e => updateField(idx, "label", e.target.value)} placeholder="Dropdown heading, e.g. 'Choose your size'" data-testid={`field-label-${field.key}`} />
                        <p className="text-[10px] text-zinc-400">Options:</p>
                        {(field.options || []).map((opt, optIdx) => (
                          <div key={optIdx} className="flex items-center gap-1.5">
                            <span className="text-[10px] text-zinc-300 w-4 text-right">{optIdx + 1}.</span>
                            <input className="brutal-input flex-1 text-xs py-1 px-2" value={opt} onChange={e => editDropdownOption(idx, optIdx, e.target.value)} placeholder={`Option ${optIdx + 1}`} data-testid={`dropdown-opt-${field.key}-${optIdx}`} />
                            <button onClick={() => removeDropdownOption(idx, optIdx)} className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600" data-testid={`dropdown-opt-remove-${field.key}-${optIdx}`}>
                              <X size={11} strokeWidth={2.5} />
                            </button>
                          </div>
                        ))}
                        <button onClick={() => addDropdownOption(idx)} className="text-[10px] font-heading font-bold uppercase text-[#FF3366] hover:text-[#e62e5c] flex items-center gap-1" data-testid={`dropdown-add-opt-${field.key}`}>
                          <Plus size={11} strokeWidth={2.5} /> Add Option
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addCustomField} className="brutal-btn brutal-btn-outline text-xs px-3 py-2 mt-3 flex items-center gap-1 w-full justify-center" data-testid="add-custom-field-btn">
                <Plus size={14} strokeWidth={2.5} /> Add Custom Field
              </button>
            </div>

            {/* Description */}
            <div>
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Welcome Message (HTML)</label>
              <textarea className="brutal-input w-full text-xs font-mono min-h-[100px] resize-y" placeholder='<p>Welcome to our event! Everyone gets a free engraved item.</p>' value={landingForm.landing_description} onChange={e => setLandingForm(f => ({ ...f, landing_description: e.target.value }))} data-testid="landing-description-input" />
            </div>

            {/* QR Code */}
            <div className="border-2 border-black p-4 bg-zinc-50">
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-3">Event QR Code</label>
              <div className="flex items-center gap-4">
                {landingEvent?.landing_qr_base64 ? (
                  <img src={`data:image/png;base64,${landingEvent.landing_qr_base64}`} alt="QR" className="w-28 h-28 border-2 border-black" data-testid="landing-qr-preview" />
                ) : (
                  <div className="w-28 h-28 border-2 border-dashed border-zinc-300 flex items-center justify-center">
                    <QrCode size={32} className="text-zinc-300" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <button onClick={() => generateQr(landingEvent?.id)} disabled={generatingQr} className="brutal-btn text-xs px-4 py-2 w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-event-qr-btn">
                    <QrCode size={14} strokeWidth={2.5} /> {generatingQr ? "Generating..." : landingEvent?.landing_qr_base64 ? "Regenerate QR" : "Generate QR Code"}
                  </button>
                  {landingEvent?.landing_qr_url && (
                    <button onClick={() => copyLandingUrl(landingEvent.landing_qr_url)} className="brutal-btn brutal-btn-outline text-xs px-4 py-2 w-full flex items-center justify-center gap-2" data-testid="copy-landing-url-btn">
                      {copiedUrl ? <Check size={14} /> : <Copy size={14} />} {copiedUrl ? "Copied!" : landingEvent.landing_qr_url}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setLandingDialogOpen(false)} className="brutal-btn brutal-btn-outline text-xs px-4 py-2">Cancel</button>
            <button onClick={saveLandingConfig} disabled={saving} className="brutal-btn text-xs px-6 py-2 disabled:opacity-50" data-testid="save-landing-config-btn">{saving ? "Saving..." : "Save Landing Page"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
